import classNames from 'classnames'
import { useCallback, useMemo, useState } from 'react'

import { Button } from 'core-components/library/button'
import { Dropdown } from 'core-components/library/dropdown'
import { Input } from 'core-components/library/inputs'
import stylesTable from 'core-components/library/list/Table.module.scss'
import { AbsolutePopup, PopPaper, TPopupVertical } from 'core-components/library/popup'
import {
  BaseCrud,
  IDataProviderFilter,
  IFilter,
  IFilterOperator,
  Logic,
  Operator,
  Static,
} from 'core-components/types'

import { IColumn, TTitleRenderFunction } from '../../types'

import { DEFAULT_FILTER_VALUE, DEFAULT_OPERATOR } from './constants'

import styles from './ThPopup.module.scss'

export interface IThPopupProps<T, R> {
  className?: string
  source: BaseCrud<T, R>
  column: IColumn
  onPriorityChange: (value: number) => void
  refresh: number
  titleRender?: TTitleRenderFunction
  headerLine?: number
}

export const ThPopup = <T, R>({
  className,
  source,
  column,
  onPriorityChange,
  refresh,
  titleRender,
  headerLine,
}: IThPopupProps<T, R>) => {
  const [currentOperation, setCurrentOperation] = useState<Operator | IFilterOperator>(
    DEFAULT_OPERATOR
  )
  const [currentFilterValue, setCurrentFilterValue] = useState<
    IDataProviderFilter | IFilter | undefined
  >(undefined)
  const [currentStringValue, setCurrentStringValue] = useState<string>(DEFAULT_FILTER_VALUE)

  const filter: IFilter | null = useMemo(() => {
    const all = (source as BaseCrud<T, R>).getFilter()
    if (all) {
      const finded = all.filters.find((f) => (f as IFilter).field === column.field)
      const findedFilters = all.filters.find(
        (f) =>
          (f as IDataProviderFilter).filters &&
          (f as IDataProviderFilter).filters.length &&
          (f as IDataProviderFilter).filters.find(
            (filter) => (filter as IFilter).field === column.field
          )
      )
      if (findedFilters) {
        setCurrentFilterValue(findedFilters)
      }
      if (finded) {
        setCurrentOperation((finded as IFilter).operator)
        setCurrentStringValue(`${(finded as IFilter).value || ''}`)
      } else {
        setCurrentStringValue(DEFAULT_FILTER_VALUE)
        setCurrentOperation(DEFAULT_OPERATOR)
      }
      return (finded as IFilter) || null
    }
    return null
  }, [refresh])

  const ddCrud = useMemo(() => {
    return new BaseCrud({
      fetcher: new Static([
        { id: Operator.CONTAINS, value: 'Contains' },
        { id: Operator.EQUAL, value: 'Equal' },
        { id: Operator.NOTEQUAL, value: 'Not equal' },
      ]),
      keyField: 'id',
    })
  }, [])

  const applyButtonDisabled = useMemo(() => {
    return (
      !currentStringValue ||
      (filter?.value === currentStringValue && filter?.operator === currentOperation)
    )
  }, [currentOperation, currentStringValue, filter])

  const apply = (closePopup: () => void) => {
    const all = (source as BaseCrud<T, R>).getFilter()
    if (all) {
      const newFilters = all.filters.filter((f) => (f as IFilter).field !== column.field)
      newFilters.push({
        field: column.field,
        operator: currentOperation,
        value: currentStringValue,
      })
      source.setFilter({ ...all, filters: newFilters })
    } else {
      source.setFilter({
        logic: Logic.AND,
        filters: [
          {
            field: column.field,
            operator: currentOperation,
            value: currentStringValue,
          },
        ],
      })
    }
    closePopup()
  }

  const renderFilterTitle = useCallback(() => {
    if (column.titleRender && headerLine !== undefined) {
      return column.titleRender({ headerLine, column })
    }

    if (titleRender) {
      return titleRender('thDisabled', column.title)
    }
    return column.title
  }, [column, headerLine, titleRender])

  const clear = () => {
    const all = (source as BaseCrud<T, R>).getFilter()
    if (all) {
      const found = all.filters.findIndex((f) => (f as IFilter).field === column.field)
      if (found > -1) {
        const newFilters = [...all.filters]
        newFilters.splice(found, 1)
        source.setFilter({ ...all, filters: newFilters })
        setCurrentStringValue('')
        return
      }
    }
    setCurrentStringValue('')
  }
  const CustomFilter = column.customFilter
  return (
    <AbsolutePopup
      rootClassName={classNames(styles.root, className || '')}
      popupClassName={styles.wrapper}
      verticalStartPosition={TPopupVertical.Bottom}
      popupContent={(contentData) => {
        return (
          <div
            onKeyDown={(e: any) => {
              if (!column.customFilter) {
                if (e.key === 'Enter') {
                  apply(contentData.action.close)
                } else if (e.key === 'Escape') {
                  clear()
                  contentData.action.close()
                }
              }
            }}
          >
            <PopPaper className={styles.paper}>
              <div
                className={styles.paper__title}
                data-testid={`${column.field}_table_filter_title`}
              >
                {renderFilterTitle()}
              </div>
              {column.priority !== undefined && (
                <div className={styles.priority}>
                  <Button
                    iconButton={'kbase kbase-arrow-left'}
                    variant="outlined"
                    disabled={column.priority === 0}
                    onClick={() => {
                      onPriorityChange(-1)
                      contentData.action.close()
                    }}
                  />
                  <div className={styles.priority__text}>
                    {titleRender && !!titleRender('thPopupEnabled', 'Priority') ? (
                      <>
                        {titleRender('thPopupEnabled', 'Priority')}
                        {`: ${column.priority}`}
                      </>
                    ) : (
                      `Priority: ${column.priority}`
                    )}
                  </div>
                  <Button
                    iconButton={'kbase kbase-arrow-right'}
                    variant="outlined"
                    onClick={() => {
                      onPriorityChange(1)
                      contentData.action.close()
                    }}
                  />
                </div>
              )}
              {!column.withoutOperator && (
                <Dropdown
                  __testId={`operator-filter-column-${column.field}`}
                  source={ddCrud}
                  caption={'value'}
                  value={currentOperation}
                  onChange={(value) => setCurrentOperation(value as Operator)}
                />
              )}
              {column.customFilter ? (
                <CustomFilter
                  currentValue={currentFilterValue}
                  onChange={(value: IDataProviderFilter) => setCurrentFilterValue(value)}
                  source={source}
                  field={column.field}
                  currentOperation={currentOperation as Operator}
                  onBlur={() => contentData.action.close()}
                />
              ) : (
                <div>
                  {titleRender && !!titleRender('thPopupFilterInput', currentStringValue) ? (
                    titleRender('thPopupFilterInput', currentStringValue, {
                      margin: 'sm',
                      value: currentStringValue,
                      onChange: (value: string) => setCurrentStringValue(value),
                    })
                  ) : (
                    <Input
                      margin={'sm'}
                      __testId={`input-filter-column-${column.field}`}
                      value={currentStringValue}
                      onChange={(value) => setCurrentStringValue(value)}
                    />
                  )}

                  <div className={styles.footer}>
                    <Button
                      __testId={`apply-button-filter-column-${column.field}`}
                      className={styles.footer__item}
                      disabled={applyButtonDisabled}
                      onClick={() => apply(contentData.action.close)}
                    >
                      {(titleRender && titleRender('thPopupEnabled', 'Apply')) || 'Apply'}
                    </Button>
                    <Button
                      className={styles.footer__item}
                      variant={'text'}
                      color={'danger'}
                      onClick={() => {
                        clear()
                        contentData.action.close()
                      }}
                    >
                      {(titleRender && titleRender('thPopupEnabled', 'Clear')) || 'Clear'}
                    </Button>
                  </div>
                </div>
              )}
            </PopPaper>
          </div>
        )
      }}
    >
      <button
        data-testid={`trigger-button-filter-column-${column.field}`}
        className={classNames(
          stylesTable.button,
          stylesTable['button--icon-btn'],
          styles.popupTrigger
        )}
        type={'button'}
      >
        <i
          className={classNames(
            stylesTable.button__icon,
            styles.triggerIcon,
            'kbase kbase-menu-dots'
          )}
        />
      </button>
    </AbsolutePopup>
  )
}
